import React, { useState } from 'react';
import { PlugAndPlaySectionProps } from '../types';
import ProductItem from './product-item';
import SectionHeading from '../common/section-heading-component';

const PlugAndPlaySection: React.FC<PlugAndPlaySectionProps> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(tabs[1]?.id || tabs[0]?.id);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  const hoveredProductData = hoveredProduct
    ? tabs
        .flatMap((tab) => tab.products)
        .find((product) => product.id === hoveredProduct)
    : null;

  const displayImage = hoveredProductData?.image || activeTabData?.image;

  if (!activeTabData) {
    return null;
  }

  const handleProductHover = (productId: string | null) => {
    setHoveredProduct(productId);
  };

  return (
    <div className="flex flex-wrap mt-40 sm:mt-20">
      <SectionHeading
        heading="Plug and Play"
        subHeading="An exhaustive portfolio of cloud based products you could use from day one for personal and business compliance"
        classname="w-full text-center mb-12"
      />

      {/* Tab Navigation */}
      <div className="flex flex-wrap justify-center mt-20 sm:mt-20 w-full">
        <div className="w-full bg-gray-100 sticky flex justify-center border-b border-gray-300 sm:justify-start sm:overflow-x-auto">
          <ul className="flex container sm:min-w-max sm:px-4">
            {tabs.map((tab) => (
              <li
                key={tab.id}
                className={`w-1/4 sm:flex-shrink-0 sm:w-auto ${
                  activeTab === tab.id ? 'border-b-2 border-blue-600' : ''
                }`}
              >
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full p-4 flex flex-col items-center hover:bg-gray-50 transition-colors sm:px-6 sm:whitespace-nowrap ${
                    activeTab === tab.id ? 'bg-white' : ''
                  }`}
                >
                  <img
                    className="w-8 h-8 mb-2"
                    src={tab.icon}
                    loading="lazy"
                    alt={tab.title}
                  />
                  <div className="flex justify-center items-center text-s-24 text-font-500 font-bold sm:text-base">
                    {tab.title}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Tab Content */}
        <div className="container w-full flex pt-8">
          <div className="w-full pt-12 flex flex-wrap md:p-4 sm:px-2 sm:py-8">
            <div className="w-1/3 p-2 md:w-1/2 sm:w-full sm:p-0 min-h-max">
              {activeTabData.products.slice(0, 2).map((product, index) => (
                <div
                  key={product.id}
                  className={index > 0 ? 'mt-8 sm:mt-2' : ''}
                >
                  <ProductItem product={product} onHover={handleProductHover} />
                </div>
              ))}
              <div className="h-40 sm:hidden"></div>
            </div>

            <div className="w-1/3 p-2 md:w-1/2 sm:w-full sm:p-0">
              {activeTabData.products.slice(2, 4).map((product, index) => (
                <div
                  key={product.id}
                  className={index > 0 ? 'mt-8 sm:mt-2' : 'sm:mt-2'}
                >
                  <ProductItem product={product} onHover={handleProductHover} />
                </div>
              ))}
              <div className="h-40 sm:hidden"></div>
            </div>

            <div className="w-1/3 flex items-start justify-center sm:hidden md:hidden">
              <div className="w-full max-w-sm h-80 flex items-center justify-center">
                <img
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                  src={displayImage}
                  alt={hoveredProductData?.title || activeTabData.title}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlugAndPlaySection;
